import DashboardLayout from '../components/DashboardLayout';
import { CircularProgress, IconButton, Tooltip, Snackbar, Alert, Box, Typography, Dialog, DialogTitle, DialogContent, Grid, Button, TextField, InputAdornment, MenuItem } from '@mui/material';
import { useState, useMemo, useEffect } from 'react';
import { endpoints } from '../api/config';
import { useCachedData } from '../hooks/useCachedData';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { getDateInputProps } from '../utils/dateConstraints';
import { BUTTON_STYLE, DIALOG_CLOSE_BUTTON_STYLE, BUTTON_VARIANT, BUTTON_SIZE, NEAR_FULLSCREEN_DIALOG_PAPER_SX } from '../constants/styles';

const InboundDataRecords = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Default date range to today's date in YYYY-MM-DD
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [startDate, setStartDate] = useState(() => {
    const saved = localStorage.getItem('inbound-data-start-date');
    return saved || todayStr;
  });
  const [endDate, setEndDate] = useState(() => {
    const saved = localStorage.getItem('inbound-data-end-date');
    return saved || todayStr;
  });
  const [successFilter, setSuccessFilter] = useState(() => {
    const saved = localStorage.getItem('inbound-data-success-filter');
    return saved || 'all'; // all | success | errors
  });

  // Build endpoint with optional date range; by default this sends today's date range
  const inboundEndpointWithRange = useMemo(() => {
    const base = endpoints.inboundDataRecords;
    if (startDate && endDate) {
      return `${base}?start_date=${startDate}&end_date=${endDate}`;
    }
    if (startDate) {
      return `${base}?start_date=${startDate}`;
    }
    if (endDate) {
      return `${base}?end_date=${endDate}`;
    }
    return base;
  }, [startDate, endDate]);

  const { data, isLoading, error, refetch } = useCachedData(inboundEndpointWithRange);



  // Dialog states (match BinTransfer UX)
  const [inboundDataOpen, setInboundDataOpen] = useState(false);
  const [inboundDataDetails, setInboundDataDetails] = useState(null);
  const [rowNetsuiteObj, setRowNetsuiteObj] = useState(null);

  const [nestedDialogOpen, setNestedDialogOpen] = useState(false);
  const [nestedDialogTitle, setNestedDialogTitle] = useState('');
  const [nestedDialogData, setNestedDialogData] = useState(null);
  const [nestedDialogLoading, setNestedDialogLoading] = useState(false);

  const [ackItemDetailsOpen, setAckItemDetailsOpen] = useState(false);
  const [ackItemDetails, setAckItemDetails] = useState([]);

  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('inbound-data-records-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });
  const [inboundDataColumnVisibility, setInboundDataColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('inbound-data-detail-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });
  const [nestedColumnVisibility, setNestedColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('inbound-nested-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });
  const [ackItemDetailsColumnVisibility, setAckItemDetailsColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('inbound-ack-item-details-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });

  // Save column visibility to localStorage
  useEffect(() => {
    localStorage.setItem('inbound-data-records-column-visibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  // Save date range and success filter to localStorage
  useEffect(() => {
    localStorage.setItem('inbound-data-start-date', startDate);
  }, [startDate]);

  useEffect(() => {
    localStorage.setItem('inbound-data-end-date', endDate);
  }, [endDate]);

  useEffect(() => {
    localStorage.setItem('inbound-data-success-filter', successFilter);
  }, [successFilter]);

  // Convert inbound_data object to flat row (keep arrays like itemData intact)
  const convertInboundObjectToRow = (obj) => {
    if (!obj || typeof obj !== 'object') return {};
    const row = {};
    const processObject = (o, prefix = '') => {
      Object.entries(o).forEach(([key, value]) => {
        const newKey = prefix ? `${prefix}_${key}` : key;
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          processObject(value, newKey);
        } else if (Array.isArray(value)) {
          // Keep itemData arrays as-is
          if (key === 'itemData' || newKey.endsWith('_itemData')) {
            row[newKey] = value;
          } else {
            row[newKey] = value.join(', ');
          }
        } else {
          row[newKey] = value;
        }
      });
    };
    processObject(obj);
    return row;
  };

  const handleRefreshClick = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
      setSnackbar({
        open: true,
        message: 'Data refreshed successfully',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to refresh data',
        severity: 'error'
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // (Removed duplicate formatColumnHeader/createBaseColumn definitions)

  // Process records to match BinTransfer UX
  const processedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((record) => {
      let username = '';
      const usernameFields = ['username','user','userName','created_by','modified_by','operator'];
      for (const f of usernameFields) {
        const v = record[f];
        if (typeof v === 'string' && v.trim()) { username = v.trim(); break; }
      }
      let inboundData = record.inbound_data ? convertInboundObjectToRow(record.inbound_data) : [];
      // derive bin name
      let binName = '-';
      if (record.inbound_data) {
        binName = record.inbound_data.binName || record.inbound_data.bin_name || '-';
      }
      // Extract containerName and inboundShipmentId from inbound_data
      let containerName = '-';
      let inboundShipmentId = '-';
      if (record.inbound_data) {
        containerName = record.inbound_data.containerName || record.inbound_data.container_name || '-';
        inboundShipmentId = record.inbound_data.inboundShipmentId || record.inbound_data.inbound_shipment_id || '-';
      }
      return {
        ...record,
        username: username || '-',
        bin_name: binName,
        container_name: containerName,
        inbound_shipment_id: inboundShipmentId,
        inbound_data: inboundData
      };
    }).sort((a,b)=> new Date(b.created_at||0)-new Date(a.created_at||0));
  }, [data]);

  // Apply success filter (All / Success / Errors)
  const filteredData = useMemo(() => {
    if (!processedData.length) return [];
    if (successFilter === 'all') return processedData;

    return processedData.filter((record) => {
      const success = record?.netsuite_response?.netsuite_acknowledgment?.success;
      const isSuccess =
        success === true || success === 'Yes' || success === 'yes' || success === 1 || success === '1';
      const isFailure =
        success === false || success === 'No' || success === 'no' || success === 0 || success === '0';

      if (successFilter === 'success') {
        return isSuccess;
      }
      if (successFilter === 'errors') {
        return isFailure;
      }
      return true;
    });
  }, [processedData, successFilter]);

  // Column helpers (like BinTransfer)
  const formatColumnHeader = (key) => key.split('_').map(w=>w.charAt(0).toUpperCase()+w.slice(1)).join(' ');
  const createBaseColumn = (key) => ({ id: key, accessorKey: key, header: formatColumnHeader(key), enableSorting: true });

  const inboundDetailColumns = useMemo(()=>{
    if (!inboundDataDetails) return [];
    const dataArray = Array.isArray(inboundDataDetails) ? inboundDataDetails : [inboundDataDetails];
    if (!dataArray.length) return [];
    // Filter out containerName, container_name, inboundShipmentId, and inbound_shipment_id from nested table
    const keys = Object.keys(dataArray[0]).filter(k=> {
      const key = k.toLowerCase();
      return key !== 'username' && 
             key !== 'containername' && 
             key !== 'container_name' && 
             key !== 'inboundshipmentid' && 
             key !== 'inbound_shipment_id';
    });
    return keys.map((key)=>{
      if (key.toLowerCase().includes('location')) {
        return { ...createBaseColumn(key), cell: info=>info.getValue(), maxSize:600, meta:{wrap:true} };
      }
      return {
        ...createBaseColumn(key),
        cell: info => {
          const value = info.getValue();
          if (value === null || value === undefined) return '-';
          if (typeof value === 'object') { return Array.isArray(value) ? (value.length>0?`${value.length} items`: 'Empty Array') : JSON.stringify(value); }
        return String(value);
      }
    };
    });
  }, [inboundDataDetails]);

  const columns = useMemo(() => {
    if (!processedData.length) return [];
    const baseColumns = [];
    const keys = Object.keys(processedData[0]).filter(k=> !['inbound_data','netsuite_response'].includes(k));
    if (keys[0]) {
      baseColumns.push({ ...createBaseColumn(keys[0]), cell: info=>{ const v=info.getValue(); if (v===null||v===undefined) return '-'; return typeof v==='object'?JSON.stringify(v):String(v);} });
    }
    baseColumns.push({ id:'username', accessorKey:'username', header:'Username', enableSorting:true, cell: info=>{ const v=info.getValue(); if (v===null||v===undefined||v==='') return '-'; return String(v).trim()||'-'; } });
    baseColumns.push({ id:'bin_name', accessorKey:'bin_name', header:'Bin Name', enableSorting:true, cell: info=> info.getValue()||'-' });
    baseColumns.push({ id:'container_name', accessorKey:'container_name', header:'Container Name', enableSorting:true, cell: info=> info.getValue()||'-' });
    baseColumns.push({ id:'inbound_shipment_id', accessorKey:'inbound_shipment_id', header:'Inbound Shipment ID', enableSorting:true, cell: info=> info.getValue()||'-' });
    // Inbound Data column with status dot
    baseColumns.push({ id:'inbound_data', ...createBaseColumn('inbound_data'), header:'Inbound Data', cell: info=>{ const value=info.getValue(); const hasData = Array.isArray(value)? value.length>0 : (value && typeof value==='object' && Object.keys(value).length>0); let success; try { const original=info.row?.original; success = original?.netsuite_response?.netsuite_acknowledgment?.success; } catch {} return (<Box sx={{ display:'flex', alignItems:'center', gap:1, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}><Box sx={{ width:10, height:10, borderRadius:'50%', backgroundColor: success===true? '#4caf50' : (success===false? '#e53935':'transparent') }} /> <Typography variant="body2">{hasData? 'View Details':'No Data'}</Typography></Box>); } });
    if (processedData[0].created_at) {
      baseColumns.push({ id:'created_at', ...createBaseColumn('created_at'), header:'Created At', cell: info=>{ const v=info.getValue(); if (!v) return '-'; try { const d=new Date(v); if (!isNaN(d)) return d.toLocaleString(); } catch(e){ return String(v);} return String(v); }});
    }
    if (processedData[0].updated_at) {
      baseColumns.push({ id:'updated_at', ...createBaseColumn('updated_at'), header:'Updated At', cell: info=>{ const v=info.getValue(); if (!v) return '-'; try { const d=new Date(v); if (!isNaN(d)) return d.toLocaleString(); } catch(e){ return String(v);} return String(v); }});
    }
    keys.slice(1).forEach(key=>{ if (!['username','created_at','updated_at','bin_name','container_name','inbound_shipment_id'].includes(key)) { const exists = baseColumns.some(c=> c.accessorKey===key); if (!exists) baseColumns.push({ ...createBaseColumn(key), cell: info=>{ const v=info.getValue(); if (v===null||v===undefined) return '-'; return typeof v==='object'? JSON.stringify(v): String(v); } }); }});
    return baseColumns;
  }, [processedData]);

  const inboundNestedDynamicColumns = useMemo(() => {
    const row = nestedDialogData?.[0];
    if (!row) return [];
    return Object.keys(row).map((k) => ({
      id: k,
      accessorKey: k,
      header: formatColumnHeader(k),
    }));
  }, [nestedDialogData]);

  const inboundAckDynamicColumns = useMemo(() => {
    const row = ackItemDetails?.[0];
    if (!row) return [];
    return Object.keys(row).map((k) => ({
      id: k,
      accessorKey: k,
      header: formatColumnHeader(k),
    }));
  }, [ackItemDetails]);

  const inboundDataRecordsMainGrid = useDataGridProTable({
    data: filteredData,
    loading: isLoading,
    error: error ? (error.message || 'Failed to load inbound data records') : null,
    title: 'Inbound Data Records',
    columns,
    onDataRefresh: handleRefreshClick,
    initialColumnVisibility: columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    onRowClick: (row) => {
      const value = row?.inbound_data;
      const hasData = Array.isArray(value)
        ? value.length > 0
        : value && typeof value === 'object' && Object.keys(value).length > 0;
      if (!hasData) return;
      setInboundDataDetails(value);
      try {
        const ns = row?.netsuite_response || null;
        setRowNetsuiteObj(ns);
      } catch {}
      setInboundDataOpen(true);
    },
    muiTableContainerProps: { sx: { height: '100%', minHeight: 0 } },
    initialSorting: [{ id: 'created_at', desc: true }],
    actionButtons: [
      <Box key="inbound-data-filters" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title="Refresh Data">
          <IconButton
            color="primary"
            onClick={handleRefreshClick}
            disabled={isRefreshing}
            sx={{ ml: 1 }}
          >
            {isRefreshing ? (
              <CircularProgress size={20} />
            ) : (
              <RefreshIcon />
            )}
          </IconButton>
        </Tooltip>
        <TextField
          select
          label="View"
          size="small"
          value={successFilter}
          onChange={(e) => setSuccessFilter(e.target.value)}
          sx={{
            minWidth: 140,
            '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
            '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
          }}
        >
          <MenuItem value="all" sx={{ fontSize: '0.8125rem' }}>All</MenuItem>
          <MenuItem value="success" sx={{ fontSize: '0.8125rem' }}>Success</MenuItem>
          <MenuItem value="errors" sx={{ fontSize: '0.8125rem' }}>Errors</MenuItem>
        </TextField>
        <TextField
          label="Start Date"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={getDateInputProps()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CalendarMonthIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 150,
            '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
            '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
          }}
        />
        <TextField
          label="End Date"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={getDateInputProps()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CalendarMonthIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 150,
            '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
            '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
          }}
        />
      </Box>,
    ],
  });

  const inboundDataRecordsDetailGrid = useDataGridProTable({
    data: Array.isArray(inboundDataDetails) ? inboundDataDetails : inboundDataDetails ? [inboundDataDetails] : [],
    columns: inboundDetailColumns,
    loading: false,
    title: 'inbound-data-detail',
    initialColumnVisibility: inboundDataColumnVisibility,
    onColumnVisibilityChange: setInboundDataColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    muiTableContainerProps: { sx: { overflow: 'auto' } },
    onRowClick: (row) => {
      if (!row) return;
      try {
        const entry = Object.entries(row).find(
          ([k, v]) => (k === 'itemData' || k.endsWith('_itemData')) && Array.isArray(v) && v.length > 0
        );
        if (entry) {
          const [key, value] = entry;
          setNestedDialogOpen(true);
          setNestedDialogLoading(true);
          setTimeout(() => {
            setNestedDialogData(value);
            setNestedDialogTitle(`Inbound Data - ${formatColumnHeader(key)}`);
            setNestedDialogLoading(false);
          }, 100);
        }
      } catch {}
    },
    disablePaper: true,
    hideHeader: true,
    error: null,
  });

  const inboundDataRecordsNestedGrid = useDataGridProTable({
    data: nestedDialogData || [],
    columns: inboundNestedDynamicColumns,
    loading: false,
    title: 'inbound-nested-item',
    initialColumnVisibility: nestedColumnVisibility,
    onColumnVisibilityChange: setNestedColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    muiTableContainerProps: { sx: { overflow: 'auto' } },
    disablePaper: true,
    hideHeader: false,
    error: null,
  });

  const inboundDataRecordsAckGrid = useDataGridProTable({
    data: ackItemDetails || [],
    columns: inboundAckDynamicColumns,
    loading: false,
    title: 'inbound-ack-items',
    initialColumnVisibility: ackItemDetailsColumnVisibility,
    onColumnVisibilityChange: setAckItemDetailsColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    muiTableContainerProps: { sx: { overflow: 'auto' } },
    disablePaper: true,
    hideHeader: false,
    error: null,
  });

  return (
    <DashboardLayout>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
          <Typography variant="h6" color="error" gutterBottom>
            Error loading data
          </Typography>
          <Typography color="textSecondary" gutterBottom>
            {error.message || 'An error occurred while loading the data'}
          </Typography>
          <IconButton
            onClick={handleRefreshClick}
            disabled={isRefreshing}
            sx={BUTTON_STYLE}
          >
            {isRefreshing ? <CircularProgress size={24} /> : <RefreshIcon />}
          </IconButton>
        </Box>
      ) : (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <MuiXDataGridSurface hook={inboundDataRecordsMainGrid} DataGridPro={DataGridPro} />
        </Box>
      )}

      {/* Inbound Data Dialog */}
      <Dialog
        open={inboundDataOpen}
        onClose={() => setInboundDataOpen(false)}
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{ sx: NEAR_FULLSCREEN_DIALOG_PAPER_SX }}
      >
        <DialogTitle>
          <Grid container alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Inbound Data Details</Typography>
            <IconButton onClick={() => setInboundDataOpen(false)} sx={DIALOG_CLOSE_BUTTON_STYLE}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {/* Show NetSuite response summary without table features */}
            {(() => {
              const ns = rowNetsuiteObj?.netsuite_response || rowNetsuiteObj;
              const ack = ns?.netsuite_acknowledgment || {};
              const safe = (v)=> v===null||v===undefined? '-' : (typeof v==='object'? JSON.stringify(v): String(v));
              if (!ns) return null;
              return (
                <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, flexShrink: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>NetSuite Response Details</Typography>
                  <Box sx={{ display:'grid', gridTemplateColumns: { xs:'1fr', sm:'220px 1fr' }, rowGap: 0.5, columnGap: 2 }}>
                    <Typography variant="body2" color="text.secondary">Message</Typography>
                    <Typography variant="body2">{safe(ns?.message)}</Typography>
                    <Typography variant="body2" color="text.secondary">Operation Time</Typography>
                    <Typography variant="body2">{safe(ns?.operation_time)}</Typography>
                    <Typography variant="body2" color="text.secondary">Success</Typography>
                    <Typography variant="body2">{ack?.success ? 'Yes' : 'No'}</Typography>
                  </Box>
                  {Array.isArray(ack?.itemDetails) && ack.itemDetails.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Button variant={BUTTON_VARIANT} size={BUTTON_SIZE} sx={BUTTON_STYLE} onClick={() => { setAckItemDetails(ack.itemDetails); setAckItemDetailsOpen(true); }}>
                        View Items ({ack.itemDetails.length})
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })()}

            {inboundDataDetails && (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <MuiXDataGridSurface hook={inboundDataRecordsDetailGrid} DataGridPro={DataGridPro} />
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Nested Item Data Dialog */}
      <Dialog
        open={nestedDialogOpen}
        onClose={() => setNestedDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{ sx: NEAR_FULLSCREEN_DIALOG_PAPER_SX }}
      >
        <DialogTitle>
          <Grid container alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{nestedDialogTitle}</Typography>
            <IconButton onClick={() => setNestedDialogOpen(false)} sx={DIALOG_CLOSE_BUTTON_STYLE}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {nestedDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : nestedDialogData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <MuiXDataGridSurface hook={inboundDataRecordsNestedGrid} DataGridPro={DataGridPro} />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Ack Item Details Dialog */}
      <Dialog
        open={ackItemDetailsOpen}
        onClose={() => setAckItemDetailsOpen(false)}
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{ sx: NEAR_FULLSCREEN_DIALOG_PAPER_SX }}
      >
        <DialogTitle>
          <Grid container alignItems="center" justifyContent="space-between">
            <Typography variant="h6">NetSuite Acknowledgment Item Details</Typography>
            <IconButton onClick={() => setAckItemDetailsOpen(false)} sx={DIALOG_CLOSE_BUTTON_STYLE}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {ackItemDetails && (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <MuiXDataGridSurface hook={inboundDataRecordsAckGrid} DataGridPro={DataGridPro} />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default InboundDataRecords; 